import React, { useState, useEffect, useMemo, useCallback } from 'react';
import moment from 'moment';
import { reaction, when } from 'mobx';
import { observer, inject } from 'mobx-react';

import shortid from 'shortid';

import { FontSizes, FontWeights, mergeStyleSets } from '@fluentui/react';

import DefaultButtonEx from '@v2/components/base/DefaultButtonEx'
import GroupPane from 'src/components/base/GroupPane';

import * as SSF from 'src/components/base/fields/salesforce';

import * as LF from 'src/components/base/fields/salesforce/LookupField';
import * as SF from 'src/components/base/fields/salesforce/SimpleField';
import * as PF from 'src/components/base/fields/salesforce/PicklistField';
import * as RTF from 'src/components/base/fields/salesforce/RichTextField';
import * as DTU from 'src/components/base/fields/dateTimeUtils';

import AssingLookup from './AssingLookup';

import * as QA from 'shared/utils/queryAnalysis';
import { runShortAction } from 'src/services/utils';
import office from 'src/services/office';

import { dateTimeNumberToMoment} from 'shared/excel/converters';
import { groupCollection, toCamelCase } from 'shared/utils/utils';


const classNames = mergeStyleSets({
  root: {
    display: 'flex',
    flexDirection: 'column',
    margin: '12px 0px 50px 0px', // Extra bottom margin because of SearchableComboBox issue
  },
  button: {
    margin: '12px 16px 0px 16px',
    width: 'auto',
  },
  emptyActions: {
    padding: '16px',
  },
  rangeTrackingCheckbox: {
    padding: '12px',
  },

  group: {
    marginTop: '12px',
  },
  groupContent: {
    display: 'flex',
    flexDirection: 'column',
    margin: '0px 16px 0px 16px',
  },

  columnGroup: {
    display: 'flex',
    flexDirection: 'column',
    margin: '0px 16px 0px 16px',
  },

  column: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginTop: '12px',
    selectors: {
      '& > .name': {
        fontWeight: FontWeights.semibold,
      },
      '& > :last-child': {
        marginTop: '6px',
      }
    }
  },
});


const Ftn = QA.FieldTypeName;

const Const = {
  groups: [
    { name: 'Lookups', types: LF.SupportedTypes },
    { name: 'Picklists', types: PF.SupportedTypes },
    { name: 'Simple', types: [...SF.SupportedTypes, Ftn.Boolean] },
    { name: 'Date and Time', types: [Ftn.Date, Ftn.Time, Ftn.DateTime] },
    { name: 'Rich Text', types: RTF.SupportedTypes }
  ],
  nonInstantTypes: [...SF.SupportedTypes, ...RTF.SupportedTypes, Ftn.Time, Ftn.DateTime],
  substitutionProps: {
    allowSubstitutions: false,
    showPicker: false,
  },
  helpLink: `https://www.xappex.com/docs/enabler4excel365/dealing-with-picklist-and-lookup-fields/`,
  formats: {
    [Ftn.DateTime]: {
      parse: ['YYYY-MM-DD[T]HH:mm:ss[Z]', 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]', 'YYYY-MM-DD HH:mm:ss'],
      format: 'YYYY-MM-DD HH:mm:ss',
      error: DTU.Errors.Time,
    },
    [Ftn.Time]: {
      parse: ['HH:mm:ss.SSS[Z]', 'HH:mm:ss.SSS', 'HH:mm:ss'],
      format: 'HH:mm:ss',
      error: DTU.Errors.Time,
    },
    [Ftn.Date]: {
      parse: ['YYYY-MM-DD[T]HH:mm:ss[Z]', 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]', 'YYYY-MM-DD'],
      format: 'YYYY-MM-DD',
      error: DTU.Errors.Date,
    },
  },
};


export const Context = observer(({ contextStore }) => {
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [columnValues, setColumnValues] = useState([]);
  const [enableColumnsGrouping, setEnableColumnsGrouping] = useState(true);

  useEffect(() => {
    const updateContextDisposer = when(
      () => contextStore.isLoaded,
      () => runShortAction(async () => {
        contextStore.loadContext = true;
        await contextStore.updateActiveWorksheetContext();
      })
    );

    const loadContextDisposer = reaction(
      () => ({ ctx: contextStore.activeWorksheetContext }),
      (ctx) => loadContext()
    );

    contextStore.updateNamedRanges();

    return () => {
      contextStore.loadContext = false;
      updateContextDisposer();
      loadContextDisposer();
    };
  }, []);

  const loadContext = async () => {
    let counter = 0;
    const columns = contextStore.activeWorksheetContext.flowsteps.flatMap(flowstep => {
      return flowstep.columns.map(column => ({
        id: shortid.generate(), valueIndex: counter++, type: column.columnData.rawField.type, ...column
      }));
    });

    columns.push(...contextStore.activeWorksheetContext.assignedLookups.map(
      column => ({
        id: shortid.generate(), assignedLookup: true, valueIndex: counter++, type: column.columnData.rawField.type, ...column
      })));

    const values = columns.map(column => {
      return { id: column.id, value: column.selection.cellValue ?? '', error: '' };
    });
    setSelectedColumns(columns);
    setColumnValues(values);
  }

  const handleChange = useCallback((columnValue) => {
    setColumnValues(prev => prev.map(x => x.id === columnValue.id ? columnValue : x));
  }, []);

  function checkForSingleCell(columns) {
    return columns.every(x => x.selection.isSingleCell && !Const.nonInstantTypes.includes(x.type));
  }

  const groups = useMemo(() => {
    const g = !enableColumnsGrouping
      ? [{ id: 'single-group', columns: selectedColumns, areAllSingleCell: checkForSingleCell(selectedColumns) }]
      : Const.groups.map(group => {
        const { name } = group;
        const columns = selectedColumns.filter(x => group.types.includes(x.type));
        const areAllSingleCell = checkForSingleCell(columns);
        return { id: name, name, columns, areAllSingleCell };
      });
    return g.filter(x => !!x.columns.length);
  }, [selectedColumns, enableColumnsGrouping]);


  const handleAssignedLookupChange = useCallback((lookupOptions) => runShortAction(async () => {
    const worksheetId = contextStore.activeWorksheetContext.id;
    let lookup = {
      worksheetId,
      ...lookupOptions,
      ...lookupOptions,
    }
    const { tableId, columnId } = lookupOptions;
    if (tableId && columnId) {
      lookup.id = `${tableId}[${columnId}]`;
    }
    else {
      const address = contextStore.activeWorksheetContext.selectedRanges[0];
      const lookupRangeName = `${lookup.objectName}_${lookup.fieldName}_${contextStore.assignedLookupsLastId + 1}`;

      const { worksheetId: idCellWorksheetId, address: idCellAddress } = lookup.idCellAddress || {};
      const idRangeName = idCellWorksheetId && idCellAddress && `${lookupRangeName}_id`;

      await contextStore.runTaskAndRefreshRanges(
        async () => {
          await office.createNamedRange(lookupRangeName, worksheetId, address.shortAddress);
          if (idRangeName) {
            await office.createNamedRange(idRangeName, idCellWorksheetId, idCellAddress);
          }
        }
      );

      lookup.rangeName = lookupRangeName;
      if (idRangeName) {
        lookup.idCellAddress = { customRange: idRangeName }
      }
      lookup.id = `${worksheetId}!${address.shortAddress}`;
    }
    runShortAction(async () => {
      await contextStore.saveAssingedLookup(lookup);
    });
  }), []);




  const Component = useMemo(() => enableColumnsGrouping ? ColumnGroupPane : ColumnGroup, [enableColumnsGrouping]);
  const isSignleCell = contextStore.activeWorksheetContext.selectedRanges?.length === 1
    && contextStore.activeWorksheetContext.selectedRanges[0].isSingleCell;


  return (
    <div className={classNames.root}>
      {
        contextStore.isLoading
          ? 'Loading'
          : groups.length
            ? groups.map(group => <Component key={group.name} {...group} values={columnValues} onChange={handleChange} />)
            : isSignleCell
              ? <AssingLookup onSave={handleAssignedLookupChange} />
              : <span className={classNames.emptyActions}>No cells with available flowstep values are currently selected.</span>

      }
    </div>
  );
});

export async function setCellValues(items) {
  await Excel.run(async (ctx) => {
    const worksheet = ctx.workbook.worksheets.getActiveWorksheet();
    items.forEach(({ value, ranges }) => {
      ranges.forEach(range => {
        const { rowIndex, columnIndex, rowCount, columnCount } = range;
        const values = Array(rowCount).fill(Array(columnCount).fill(value));
        worksheet.getRangeByIndexes(rowIndex, columnIndex, rowCount, columnCount).values = values;
      })
    });

    await ctx.sync();
  });
}

async function setAssignedLookupsId(items, contextStore) {
  const cellItems = [];
  const columnItems = [];
  items.forEach(({ columnData, value }) => {
    const { idCellAddress, idCellColumn, ranges } = columnData;
    if (idCellAddress) {
      cellItems.push({ address: idCellAddress, value });
    }
    else if (idCellColumn && ranges) {
      const { tableId, lookupColumnId, idColumnId } = idCellColumn;
      columnItems.push({ columnId: lookupColumnId, tableId, idColumnId, ranges, value });
    }
  });
  await setAssignedLookupsIdCellValues(cellItems, contextStore);
  await setAssignedLookupsColumnValues(columnItems);
}

async function setAssignedLookupsIdCellValues(items, contextStore) {
  let isCustomRangesUpdated = false;
  const itemsWithWorksheets = (await Promise.all(items.map(async ({ value, address }) => {
    let { customRange, worksheetId, address: localAddress } = address;
    const getWorksheetParams = {};
    if (customRange) {
      if (!isCustomRangesUpdated) {
        await contextStore.updateNamedRanges();
        isCustomRangesUpdated = true;
      }
      const rangeAddress = contextStore.getNamedRangeAddress(customRange);
      if (!rangeAddress) {
        return null;
      }
      localAddress = rangeAddress.getFirstCell().shortAddress;
      getWorksheetParams.name = rangeAddress.worksheetName;
    }
    else if (worksheetId) {
      getWorksheetParams.id = worksheetId;
    }
    const worksheet = contextStore.getWorksheet(getWorksheetParams);
    if (!worksheet) {
      return null;
    }
    return { value, worksheet, address: localAddress };
  }))).filter(item => !!item);
  const itemsByWorksheets = groupCollection(itemsWithWorksheets, (itemOrNull, get) => {
    if (itemOrNull) {
      const { worksheet, ...item } = itemOrNull;
      const uniqueWorksheet = get(worksheet.id, () => ({ worksheetId: worksheet.id, items: [] }));
      uniqueWorksheet.items.push(item);
    }
  });

  await Excel.run(async (context) => {
    for (let { worksheetId, items } of itemsByWorksheets) {
      const worksheet = context.workbook.worksheets.getItem(worksheetId);
      items.forEach(({ address, value }) => {
        worksheet.getRange(address).values = [[value]];
      });
    }
  });
}

async function setAssignedLookupsColumnValues(items) {
  await Excel.run(async (ctx) => {
    //Ranges can only be selected within one sheet
    let worksheet;
    const idRanges = [];

    for (let { tableId, columnId, idColumnId, ranges, value } of items) {
      const { table, columns } = await office.getTable(tableId, ctx);
      if (!table || !columns) { continue; }

      let idColumn, column;
      columns.forEach(col => {
        if (col.id === idColumnId) {
          idColumn = col;
        }
        else if (col.id === columnId) {
          column = col;
        }
      });

      if (!column || !idColumn) { continue; }


      column.load('index');
      idColumn.load('index');

      if (!worksheet) {
        table.load('worksheet');
      }

      await ctx.sync();

      if (!worksheet) {
        worksheet = table.worksheet;
      }

      const offset = idColumn.index - column.index;
      ranges.forEach(selectedRange => {
        idRanges.push({ range: selectedRange.getOffsetRange(0, offset), value });
      });
    }
    idRanges.forEach(({ range, value }) => worksheet.getRange(range.shortAddress).values = new Array(range.rowCount).fill([value]));
    await ctx.sync();
  });
}

function ColumnGroupPane({ name, ...rest }) {
  return (
    <GroupPane className={classNames.group} title={name} collapsed={false}>
      <ColumnGroup {...rest} />
    </GroupPane>
  );
}


function _ColumnGroupComponent({ contextStore, columns, values: allValues, areAllSingleCell, onChange }) {
  const values = columns.map(x => allValues[x.valueIndex]);

  function handleChange(newValue, column) {
    const { assignedLookup } = column;
    let value = newValue.value;
    let id;
    if (value) {
      if (assignedLookup) {
        id = value[LF.ValueKey.Id];
        value = value[LF.ValueKey.Field];
        newValue = { ...newValue, value, lookupId: id };
      }
      if (areAllSingleCell && !newValue.error) {
        const { selection: { intersections: ranges } } = column;
        runShortAction(async () => {
          await setCellValues([{ ranges, value }]);
          if (assignedLookup) {
            await setAssignedLookupsId([{ columnData: { ...column.columnData, ranges }, value: id }], contextStore);
          }
        });
      }
    }
    onChange(newValue);
  }

  function handleApplyClick() {
    applyChanges();
  }

  function onLookupDelete(column) {
    const { lookupId } = column.columnData;
    runShortAction(async () => {
      await contextStore.deleteAssignedLookup(lookupId);
    });
  }

  function applyChanges() {
    const valueItems = [];
    const idItems = [];
    columns.forEach(({ selection: { intersections: ranges }, columnData }, index) => {
      const value = values[index];
      valueItems.push({ ranges, value: value.value });
      if (value.lookupId) {
        idItems.push({ value: value.lookupId, columnData: { ...columnData, ranges } })
      }
    });

    runShortAction(async () => {
      await setCellValues(valueItems);
      await setAssignedLookupsId(idItems, contextStore);
    });
  }

  return (
    <>
      <div className={classNames.columnGroup}>
        {columns.map(item => {
          const { valueIndex, id } = item;
          const value = allValues[valueIndex];
          return (
            <Column key={id} column={item} data={value} onChange={(value) => handleChange(value, item)} onLookupDelete={() => onLookupDelete(item)} />
          );
        })
        }
      </div>
      {!areAllSingleCell &&
        <DefaultButtonEx className={classNames.button} text={'Apply'}
          primary={true} onClick={handleApplyClick} />
      }
    </>
  );
}

const ColumnGroup = inject('contextStore')(observer(_ColumnGroupComponent));

function Column({ column, data, onChange, onLookupDelete }) {
  const { columnData, headerName, selection, assignedLookup } = column;
  const { intersections } = selection;
  // console.log('Column: ', columnData);

  const name = useMemo(() => {
    const addresses = intersections.map(x => x.shortAddress).join(', ');
    let header = headerName;
    if (!assignedLookup) {
      header = `'${header}'`
    }
    return `${header} at ${addresses}`;
  }, [column, assignedLookup]);

  function handleChange({ value, error }) {
    onChange({ ...data, value });
  }

  const handleLookupDelete = useCallback(() => onLookupDelete(), []);

  const { value } = data;
  const converters = useMemo(() => getConverters(columnData.rawField), [columnData]);

  return (
    <div className={classNames.column}>
      <span className="name">{name}</span>
      <SSF.Field
        {...columnData}
        converters={converters}
        substitutionProps={Const.substitutionProps}
        value={value}
        onChange={handleChange}
      />
      {!!assignedLookup && <DefaultButtonEx className={''} text={'Delete'} onClick={handleLookupDelete} />}
    </div>
  );
}



function parseExcelValue(value, formats) {
  const number = +value;
  if (!Number.isNaN(number)) {
    return { value: dateTimeNumberToMoment(number) };
  }

  const toParse = typeof (value) === 'string' ? value : '';
  return DTU.TextConverters.parse(toParse, formats.parse, formats.error)
}


function getConverters(rawField) {
  const fmt = Const.formats[rawField.type];
  if (!fmt) { return {}; }

  return {
    parse: (excelValue) => parseExcelValue(excelValue, fmt),
    format: (momentValue) => DTU.TextConverters.format(momentValue, fmt.format, fmt.error),
  };
}


export default inject('contextStore')(Context);